import { useEffect, type Dispatch } from 'react';

import type { AnyAction } from '@reduxjs/toolkit';

import { useSoftBoard } from '@whelk/hooks/src/useSoftBoard';
import { isIOS, isMobileByUA, isIos13 } from '@whelk/utils/src/checkUA';
import { focusInput } from '@whelk/utils/src/focus';
import { fixOnBlur, fixBottomInAppBrowser } from '@whelk/utils/src/fxxkSafari';
import { scrollToBottom } from '@whelk/utils/src/scroll';

// dom : 操作 dom 的帮助函数
export const selectElement = (selector: string) =>
  document.querySelector(selector) as HTMLElement | null;

//! 修复兼容性问题的 hooks

export const useFixRubber = (
  inputSelector: string,
  originInnerHeightRef: React.RefObject<number>,
) => {
  // 修复橡皮筋效果，主要是软键盘弹起的时候，整个页面可以上下滑动的问题。
  // 主要监听 input box 里面的滑动事件。
  const onInputBoxTouchMove = (ev: TouchEvent) => {
    // 阻止冒泡到 layout 上，layout 上会监听 touchmove 事件，然后收起软键盘
    // 这时候不应该收起软键盘

    // 但是此时 ios 还会触发橡皮筋的效果，这时候阻止橡皮筋。
    ev.stopPropagation();
    // ⚠️老版本 ipad ios 中，无法阻止冒泡，所以外层 layout 监听 touchmove 要排除一下。
    // ⚠️之所以没去掉这个阻止冒泡，是为了怕误伤其他兼容性。。。 :)
    if (isIOS) {
      // 最终方案，混合 plan A 和 planB
      // 6行内，一律禁止滑动效果，ios 滑动选择特性也不要。
      // 6行以上，ios 滑动选择特性保留，但是橡皮筋特性根据情况重置页面滚动。
      // PlanA 和 PlanB 不会同时成立。

      //planA 只有等输入框大于 6 行出现滚动条时，才不阻止。
      // 但这样 ios 的滑动选择特性就没了，优点是可以在少文字的情况下避免橡皮筋特效。
      const inputEl = document.querySelector(inputSelector)! as HTMLTextAreaElement;

      if (inputEl.scrollHeight <= inputEl.clientHeight) {
        ev.preventDefault();
        return false;
      }

      // planB: 在橡皮筋特效出现的时候，重置滚动位置
      // 缺点是会闪一下，优点是 ios 的滑动选择特性可以保留。

      if (!originInnerHeightRef.current) return;
      if (window.innerHeight !== originInnerHeightRef?.current) {
        // 第一次阻止默认行为没有用。直到第一次松手才有效，所以最开始先取消变更
        const fixY = document.body.scrollHeight - originInnerHeightRef?.current;
        window.scrollTo(0, fixY);
        // 这时候阻止默认行为兜底(虽然看起来没用，但指不定什么版本的 ios 就能用了呢）。
        ev.preventDefault();
        return false;
      }
    }
  };

  // 绑定监听
  useEffect(() => {
    if (!isIOS) return;
    selectElement(inputSelector)?.addEventListener('touchmove', onInputBoxTouchMove, {
      passive: false,
    });
    return () => {
      if (!isIOS) return;
      selectElement(inputSelector)?.removeEventListener('touchmove', onInputBoxTouchMove);
    };
  }, []);
};

// 修复软键盘弹出不了的问题
export const useFixCantFocus = (inputSelector: string) => {
  const onInputBoxTouchEnd = (ev: TouchEvent) => {
    // 上传按钮不需要处理
    const upFileBtn = selectElement('#input-up-file');
    if (upFileBtn?.contains(ev.target as Node)) {
      return;
    }
    // // 电话模式按钮不需要处理
    // const telModeBtn = selectElement('#tel-mode-btn');
    // if (telModeBtn?.contains(ev.target as Node)) {
    //   return;
    // }

    const stopCreateBtn = selectElement('#stop-create-btn');
    if (stopCreateBtn?.contains(ev.target as Node)) {
      return;
    }

    const miniUploadBtn = selectElement('#mini-upload-btn');
    if (miniUploadBtn?.contains(ev.target as Node)) {
      return;
    }

    // 安卓不需要这个（不然会无法在外面选中文字）， ios 需要（不然软键盘会闪烁）。
    // 因为安卓的这个事件，不会收起软键盘，ios 会。
    if (isIOS && !isIos13) {
      ev.preventDefault();
    }
    //排除图标区域
    //排除图标区域
    const inputIcons = document.querySelectorAll('[data-input-icon]');
    if (inputIcons.length > 0) {
      for (const eachIcon of Array.from(inputIcons)) {
        if (eachIcon.contains(ev.target as Node)) {
          return;
        }
      }
      focusInput();
      setTimeout(() => {
        focusInput();
      }, 0);
      setTimeout(() => {
        focusInput();
      }, 50);
    }
  };

  // 绑定监听
  useEffect(() => {
    if (!isMobileByUA) return;
    selectElement(inputSelector)?.addEventListener('touchend', onInputBoxTouchEnd, {
      passive: false,
    });
    return () => {
      if (!isMobileByUA) return;
      selectElement(inputSelector)?.removeEventListener('touchend', onInputBoxTouchEnd);
    };
  }, []);
};

// 修复在应用内置浏览器的底部展示短一块的问题
export const useFixBottomShow = () => {
  useEffect(() => {
    if (!isMobileByUA) return;
    fixBottomInAppBrowser();
  }, []);
};

// 处理软键盘弹出相关
export const useFixSoftKeyboard = (
  dispatch: Dispatch<AnyAction>,
  setSoftKeyboardOpen: any,
  originInnerHeightRef: any,
) => {
  const onOpenSoftBoard = () => {
    dispatch(setSoftKeyboardOpen(true));
    setTimeout(() => {
      if (originInnerHeightRef.current === 0) {
        originInnerHeightRef.current = window.innerHeight;
      }
      scrollToBottom(document.querySelector('#main-container'));
      // 某些版本的 ipad 上 100ms 居然不够。。。
    }, 500);
  };
  const onCloseSoftBoard = () => {
    dispatch(setSoftKeyboardOpen(false));
    // 关闭软键盘，手动触发 resize 时输入框归位。
    fixOnBlur();
  };
  // 这里面只会在需要的设备上监听。
  useSoftBoard('#chat-input', onOpenSoftBoard, onCloseSoftBoard);
};
